'use strict';

export class DOM {

	/**
	 * Function to observe elements
	 *
	 * @param {string} selector
	 * @param {callback} callback
	 */
	static observeElements (selector, callback) {
		const elements = document.querySelectorAll(selector);

		if (elements.length === 0) {
			return;
		}

		if ('IntersectionObserver' in window) {
			const observer = new IntersectionObserver((entries, observer) => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						callback(entry.target);
						observer.unobserve(entry.target); // Stop observing once it's in view
					}
				});
			});

			elements.forEach(element => observer.observe(element));
		} else {
			// Fallback for browsers that do not support IntersectionObserver
			const checkVisibility = () => {
				elements.forEach(element => {
					const rect = element.getBoundingClientRect();
					if (rect.top < window.innerHeight && rect.bottom >= 0) {
						callback(element);
					}
				});
			};

			window.addEventListener('scroll', checkVisibility);
			window.addEventListener('resize', checkVisibility);
			checkVisibility(); // Initial check
		}
	}

	/**
	 * Function to add event listeners easily, allowing use of multiple event listeners for multiple elements by selector
	 *
	 * @param {string} event - The event type(s) to listen for, separated by commas
	 * @param {string|Element|Window|Document} selector - The CSS selector, element, window, or document to attach the event to
	 * @param {Function} callback - The callback function to execute when the event is triggered
	 * @param {Element|Window|Document} [eventElement=null] - The element to attach the event listener to
	 * @param {any} [options=null] - Options to pass to the event listener
	 *
	 * @return {callback}
	 */
	static addEvent(event, selector, callback, eventElement = null, options = null) {
		let element = null;

		if (selector instanceof Element) {
			element = selector;
		} else {
			element = document;

			if (eventElement) {
				if (eventElement === window){
					element = eventElement;
				} else if(eventElement instanceof Element) {
					element = eventElement;
				} else if (eventElement instanceof Document) {
					element = document;
				}
			}
		}

		const eventCallback = (event) => {
			if (selector instanceof Element) {
				callback(event, selector)
			} else {
				if (this.compareElement(event.target, selector)){
					callback(event, this.compareElement(event.target, selector));
				}
			}
		};

		const events = event.split(',');

		for (const evt of events) {
			element.removeEventListener(evt, eventCallback, options);
			element.addEventListener(evt, eventCallback, options);
		}

		return () => {
			for (const evt of events) {
				element.removeEventListener(evt, eventCallback, options);
			}
		}
	}

	/**
	 * Check if the passed element matches the passed selector, or is a descendant of an element that matches the selector
	 *
	 * @param {HTMLElement|Element} element - The element to compare
	 * @param {string} selector - The CSS selector to compare against
	 * @returns {HTMLElement|Element|null} The matched element or null if no match is found
	 */
	static compareElement(element, selector) {
		if (!element || !selector) return null;

		if (selector instanceof Document || selector instanceof Window) {
			return element;
		}

		if (element === selector) {
			return element;
		} else if (element.matches(selector)) {
			return element;
		} else {
			return element.closest(selector) || null;
		}
	}

	/**
	 * Function to select elements within a specified parent element
	 *
	 * @example DOM.getElement('p', document, true) // list of all p tags in the document
	 * @example DOM.getElement('p,a', document, true) // list of all p and a tags in the document
	 * @example DOM.getElement(['p', 'a'], document, true) // list of all p and a tags in the document
	 * @example DOM.getElement(['p', 'a']) // first a or p tag in the document
	 * @example DOM.getElement('p') // first p tag in the document
	 * @example DOM.getElement('p,a') // first a or p tag in the document
	 * @example DOM.getElement('div', container) // first div tag in the container
	 *
	 * @param {string|string[]} selector - The CSS selector(s) to use for selection
	 * @param {Document|HTMLElement} [element=document] - The parent element to search within
	 * @param {boolean} [list=false] - Whether to return a list of matching elements
	 * @returns {HTMLElement[]|HTMLElement} The matched element(s)
	 *
	 * @throws DOMException if something went wrong with selection
	 */
	static getElement(selector, element = document, list = false) {
		if (!element) {
			return null
		}

		if (Array.isArray(selector)) {
			selector = selector.join(',');
		}

		if (list) {
			return [...element.querySelectorAll(selector)];
		}

		return element.querySelector(selector);
	}

	/**
	 * Short usage of getElement single
	 *
	 * @example DOM.el(['p', 'a']) // first a or p tag in the document
	 * @example DOM.el('p') // first p tag in the document
	 * @example DOM.el('p,a') // first a or p tag in the document
	 * @example DOM.el('div', container) // first div tag in the container
	 *
	 * @param {string|string[]} selector - The CSS selector(s) to use for selection
	 * @param {Document|HTMLElement} [element=document] - The parent element to search within
	 * @returns {HTMLElement} The matched element
	 *
	 * @throws DOMException if something went wrong with selection
	 */
	static el(selector, element = document) {
		return this.getElement(selector, element)
	}

	/**
	 * Short usage of getElement multiple
	 *
	 * @example DOM.els('p', document) // list of all p tags in the document
	 * @example DOM.els('p,a', document) // list of all p and a tags in the document
	 * @example DOM.els(['p', 'a'], document) // list of all p and a tags in the document
	 *
	 * @param {string|string[]} selector - The CSS selector(s) to use for selection
	 * @param {Document|HTMLElement} [element=document] - The parent element to search within
	 * @returns {HTMLElement[]} The matched elements
	 *
	 * @throws DOMException if something went wrong with selection
	 */
	static els(selector, element = document) {
		return this.getElement(selector, element, true)
	}

	/**
	 * Get the most parent element by selector of the passed element
	 *
	 * @param {Element} element - The starting element
	 * @param {String} parentSelector - The CSS selector of the parent element
	 * @return {Element} The most parent element
	 */
	static mostParent(element, parentSelector) {
		let parent = element.closest(parentSelector);

		while (parent) {
			if (parent && element !== parent) {
				element = parent;
				parent = element.closest(parentSelector);
			} else {
				break;
			}
		}

		return element;
	}

	/**
	 * Get the children of an element
	 *
	 * @param {Element} element - The parent element
	 * @returns {Element[]} The child elements
	 */
	static children(element) {
		return [...(element?.children || [])]
	}

	/**
	 * Get all parent elements matching a selector
	 *
	 * @param {Element} element - The starting element
	 * @param {String} parentSelector - The CSS selector of the parent elements
	 * @returns {Element[]} The parent elements
	 */
	static parents(element, parentSelector) {
		const elements = [];
		let parent = element.closest(parentSelector);

		while (parent) {
			if (parent && element !== parent) {
				element = parent;
				parent = element.closest(parentSelector);
				elements.push(parent);
			} else {
				break;
			}
		}

		return elements;
	}

	/**
	 * Check if a string contains HTML
	 *
	 * @param {string} string - The string to check
	 * @returns {boolean} True if the string contains HTML, false otherwise
	 */
	static isHtmlString(string) {
		let el = document.createElement('div');
		el.innerHTML = string;

		return this.children(el).length > 0;
	}

	/**
	 * Trigger an event on an element or elements
	 *
	 * @param {HTMLElement|HTMLElement[]|string} element - The element or CSS selector to trigger the event on
	 * @param {string} eventType - The type of event to trigger
	 * @param {Object} [options={}] - Additional options for the event
	 * @param {boolean} [multiple=false] - Whether to trigger the event on multiple elements
	 * @param {any} [eventClass=null] - Whether to trigger the event on multiple elements
	 */
	static triggerEvent(element, eventType, options = {}, multiple = false, eventClass = null) {
		let event;

		if (!eventClass) {
			// Automatically create the appropriate event instance based on the event type
			switch (eventType) {
				case 'click':
				case 'dblclick':
				case 'mousedown':
				case 'mouseup':
				case 'mouseover':
				case 'mouseout':
				case 'mousemove':
				case 'mouseenter':
				case 'mouseleave':
					options = {
						bubbles: true,
						cancelable: true,
						view: window,
						...options,
					};
					event = new MouseEvent(eventType, options);
					break;
				case 'keydown':
				case 'keypress':
				case 'keyup':
					options = {
						bubbles: true,
						cancelable: true,
						view: window,
						...options,
					};
					event = new KeyboardEvent(eventType, options);
					break;
				case 'focus':
				case 'blur':
				case 'focusin':
				case 'focusout':
					options = {
						bubbles: true,
						cancelable: true,
						view: window,
						...options,
					};
					event = new FocusEvent(eventType, options);
					break;
				case 'input':
				case 'change':
				case 'submit':
				case 'reset':
					options = {
						bubbles: true,
						cancelable: true,
						...options,
					};
					event = new Event(eventType, options);
					break;
				default:
					options = {
						bubbles: true,
						cancelable: true,
						...options,
					};
					// Default to a generic Event instance
					event = new Event(eventType, options);
					break;
			}
		} else {
			options = {
				bubbles: true,
				cancelable: true,
				...options,
			};
			event = new eventClass(eventType, options)
		}

		if (typeof element === "string") {
			if (multiple) {
				element = DOM.els(element);
			} else {
				element = [DOM.el(element)];
			}
		} else if (!element?.forEach) {
			element = [element];
		}

		element?.forEach(el => el.dispatchEvent(event));
	}

	/**
	 * Insert element after passed node
	 *
	 * @param referenceNode{HTMLElement}
	 * @param newNode{HTMLElement}
	 */
	static insertAfter(referenceNode, newNode) {
		referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
	}

	/**
	 * Insert element before passed node
	 *
	 * @param referenceNode{HTMLElement}
	 * @param newNode{HTMLElement}
	 */
	static insertBefore(referenceNode, newNode) {
		referenceNode.parentNode.insertBefore(newNode, referenceNode);
	}

	static create(nodeName, attrs = {}, children = []) {
		const element = document.createElement(nodeName);
		Object.keys(attrs).forEach((attrKey) => {
			let attrName = attrKey === 'classname' || attrKey === 'className' ? 'class' : attrKey;
			attrName = attrName.replace(/([a-z])([A-Z])/g, '$1-$2')
				.toLowerCase();

			let attrValue = attrs[attrKey];
			if (attrName === "class") {
				attrValue = Array.isArray(attrValue) ? attrValue.filter(Boolean).join(' ') : attrValue.trim().replace(/\s+/g, ' ');
			} else if (typeof attrValue === 'object') {
				attrValue = JSON.stringify(attrs[attrKey])
			}
			element.setAttribute(attrName, attrValue);
			element[attrName] = attrValue
		})
		if (!Array.isArray(children)) {
			children = [children];
		}
		children.forEach(child => {
			if (!child) return;

			if (typeof child !== "object") {
				element.append(child)
			} else if (child instanceof Element) {
				element.append(child)
			} else {
				element.append(JSON.stringify(child))
			}
		})

		return element;
	}

}

function observeElements(selector, elementCallback) {
	return DOM.observeElements(selector, elementCallback);
}
function addEvent(event, selector, callback, eventElement = null, options = null) {
	return DOM.addEvent(event, selector, callback, eventElement, options);
}
function compareElement(element, selector) {
	return DOM.compareElement(element, selector);
}
function getElement(selector, element = null, list = false) {
	return DOM.getElement(selector, element, list);
}
function el(selector, element = document) {
	return DOM.el(selector, element);
}
function els(selector, element = document) {
	return DOM.els(selector, element);
}
function mostParent(element, parentSelector) {
	return DOM.mostParent(element, parentSelector)
}
function children(element) {
	return DOM.children(element)
}
function parents(element, parentSelector) {
	return DOM.parents(element, parentSelector)
}
function isHtmlString(string) {
	return DOM.isHtmlString(string)
}

function triggerEvent(element, eventType, options = {}, multiple = false) {
	return DOM.triggerEvent(element, eventType, options, multiple)
}

export {
	observeElements,
	addEvent,
	compareElement,
	getElement,
	el,
	els,
	mostParent,
	children,
	parents,
	isHtmlString,
	triggerEvent
};
